import React, { useState, useEffect } from 'react';
import MarkdownReader from '../components/MarkdownReader';

const ManifestPage = () => {
  const [manifestData, setManifestData] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load manifest data via fetch from public directory
    fetch('/clara360_manifest.json')
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setManifestData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Fehler beim Laden des Manifests:', error);
        setError(error.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-slate-300">Lade Manifest...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-400 mb-2">Manifest konnte nicht geladen werden</h2>
          <p className="text-slate-300 mb-4">Fehler: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg"
          >
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }

  if (!manifestData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-300">Keine Manifest-Daten verfügbar</p>
        </div>
      </div>
    );
  }

  const sections = [
    // Core system sections from JSON
    { id: 'overview', name: 'Übersicht', icon: '📋', type: 'json' },
    
    // New consolidated markdown sections (6 total)
    { id: 'system-overview', name: 'System Overview', icon: '📋', type: 'markdown', file: 'system-overview.md' },
    { id: 'vision-strategy', name: 'Vision & Strategy', icon: '🎯', type: 'markdown', file: 'vision-strategy.md' },
    { id: 'architecture', name: 'Architecture & Components', icon: '🏗️', type: 'markdown', file: 'architecture.md' },
    { id: 'components', name: 'Components', icon: '⚙️', type: 'markdown', file: 'components.md' },
    { id: 'ui-ux-system', name: 'UI/UX System', icon: '🎨', type: 'markdown', file: 'ui-ux-system.md' },
    { id: 'features-integrations', name: 'Features & Integrations', icon: '🚀', type: 'markdown', file: 'features-integrations.md' },
    { id: 'development-lessons', name: 'Development & Lessons', icon: '📚', type: 'markdown', file: 'development-lessons.md' },
    { id: 'roadmap', name: 'Roadmap', icon: '🗺️', type: 'markdown', file: 'roadmap.md' }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h2 className="text-2xl font-bold text-yellow-400 mb-4">
          {manifestData.clara360_manifest_consolidated.system_info.name} v{manifestData.clara360_manifest_consolidated.version}
        </h2>
        <p className="text-slate-300 mb-4">{manifestData.clara360_manifest_consolidated.system_info.description || 'Comprehensive property management dashboard with AI-powered tenant communication and FinAPI banking integration'}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{manifestData.clara360_manifest_consolidated.environment}</div>
            <div className="text-sm text-slate-400">Status</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{manifestData.clara360_manifest_consolidated.slots ? Object.keys(manifestData.clara360_manifest_consolidated.slots).length : 5}</div>
            <div className="text-sm text-slate-400">Aktive Slots</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">13</div>
            <div className="text-sm text-slate-400">Komponenten</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">
              {new Date(manifestData.clara360_manifest_consolidated.last_updated).toLocaleDateString('de-DE')}
            </div>
            <div className="text-sm text-slate-400">Letztes Update</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderVision = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-xl font-bold text-yellow-400 mb-4">Mission</h3>
        <p className="text-slate-300 mb-6">{manifestData.vision.mission}</p>
        
        <h3 className="text-xl font-bold text-yellow-400 mb-4">Ziele</h3>
        <ul className="space-y-2 mb-6">
          {manifestData.vision.goals.map((goal, index) => (
            <li key={index} className="flex items-start">
              <span className="text-green-400 mr-2">✓</span>
              <span className="text-slate-300">{goal}</span>
            </li>
          ))}
        </ul>

        <h3 className="text-xl font-bold text-yellow-400 mb-4">Zielgruppen</h3>
        <div className="grid grid-cols-2 gap-4">
          {manifestData.vision.target_users.map((user, index) => (
            <div key={index} className="bg-slate-700 rounded-lg p-3">
              <span className="text-slate-300">{user}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderArchitecture = () => (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-yellow-400 mb-4">Frontend</h3>
          <div className="space-y-3">
            {Object.entries(manifestData.architecture.frontend).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-slate-400 capitalize">{key.replace('_', ' ')}:</span>
                <span className="text-slate-300">{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-yellow-400 mb-4">Backend</h3>
          <div className="space-y-3">
            {Object.entries(manifestData.architecture.backend).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-slate-400 capitalize">{key.replace('_', ' ')}:</span>
                <span className="text-slate-300">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-xl font-bold text-yellow-400 mb-4">Integrationen</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(manifestData.architecture.integrations).map(([key, value]) => (
            <div key={key} className="bg-slate-700 rounded-lg p-3">
              <div className="font-semibold text-slate-200 capitalize">{key}</div>
              <div className="text-sm text-slate-400">{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSlots = () => (
    <div className="space-y-6">
      <div className="grid gap-4">
        {Object.entries(manifestData.slots).map(([key, slot]) => (
          <div key={key} className="bg-slate-800 rounded-lg p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-yellow-400">{slot.component}</h3>
              <span className={`px-3 py-1 rounded-full text-sm ${
                slot.status === 'active' ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'
              }`}>
                {slot.status}
              </span>
            </div>
            <p className="text-slate-400 mb-3">Route: <span className="text-blue-400">{slot.route}</span></p>
            <div className="flex flex-wrap gap-2">
              {slot.features.map((feature, index) => (
                <span key={index} className="bg-slate-700 px-3 py-1 rounded-full text-sm text-slate-300">
                  {feature}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderComponents = () => (
    <div className="space-y-6">
      {Object.entries(manifestData.components).map(([type, components]) => (
        <div key={type} className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-yellow-400 mb-4 capitalize">{type}</h3>
          <div className="grid md:grid-cols-3 gap-3">
            {components.map((component, index) => (
              <div key={index} className="bg-slate-700 rounded-lg p-3 text-center">
                <span className="text-slate-300">{component}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderDeployment = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-xl font-bold text-yellow-400 mb-4">Deployment Info</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Production URL:</span>
            <a href={manifestData.deployment.production_url} target="_blank" rel="noopener noreferrer" 
               className="text-blue-400 hover:text-blue-300">
              {manifestData.deployment.production_url}
            </a>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">GitHub Repo:</span>
            <span className="text-slate-300">{manifestData.deployment.github_repo}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Branch:</span>
            <span className="text-slate-300">{manifestData.deployment.branch}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Auto Deploy:</span>
            <span className={manifestData.deployment.auto_deploy ? 'text-green-400' : 'text-red-400'}>
              {manifestData.deployment.auto_deploy ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Letztes Deployment:</span>
            <span className="text-slate-300">
              {new Date(manifestData.deployment.last_deploy).toLocaleString('de-DE')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-xl font-bold text-yellow-400 mb-4">DSGVO Compliance</h3>
        <div className="mb-4">
          <span className={`px-3 py-1 rounded-full text-sm ${
            manifestData.compliance.dsgvo.status === 'compliant' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
            {manifestData.compliance.dsgvo.status === 'compliant' ? 'DSGVO-konform' : 'Nicht konform'}
          </span>
        </div>
        <h4 className="font-semibold text-slate-200 mb-3">Maßnahmen:</h4>
        <ul className="space-y-2 mb-6">
          {manifestData.compliance.dsgvo.measures.map((measure, index) => (
            <li key={index} className="flex items-start">
              <span className="text-green-400 mr-2">✓</span>
              <span className="text-slate-300">{measure}</span>
            </li>
          ))}
        </ul>

        <h3 className="text-xl font-bold text-yellow-400 mb-4">Sicherheit</h3>
        <div className="space-y-3">
          {Object.entries(manifestData.compliance.security).map(([key, value]) => (
            <div key={key} className="flex justify-between">
              <span className="text-slate-400 capitalize">{key.replace('_', ' ')}:</span>
              <span className="text-slate-300">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderRoadmap = () => (
    <div className="space-y-6">
      {Object.entries(manifestData.roadmap).map(([version, features]) => (
        <div key={version} className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-yellow-400 mb-4">{version}</h3>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <span className="text-blue-400 mr-2">→</span>
                <span className="text-slate-300">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const renderSection = () => {
    const currentSection = sections.find(s => s.id === activeSection);
    
    // Handle markdown sections
    if (currentSection && currentSection.type === 'markdown') {
      return (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <MarkdownReader 
            filePath={currentSection.file} 
            className="min-h-[400px]"
          />
        </div>
      );
    }
    
    // Handle JSON sections (existing functionality)
    switch (activeSection) {
      case 'overview': return renderOverview();
      case 'vision': return renderVision();
      case 'architecture': return renderArchitecture();
      case 'slots': return renderSlots();
      case 'components': return renderComponents();
      case 'deployment': return renderDeployment();
      case 'compliance': return renderCompliance();
      case 'roadmap': return renderRoadmap();
      default: return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="p-6 border-b border-slate-700">
        <h1 className="text-2xl font-bold">📋 System Manifest</h1>
        <p className="text-slate-300 mt-2">Clara360 v3.1 - Vollständige Systemdokumentation</p>
      </div>

      {/* Navigation */}
      <div className="p-6 border-b border-slate-700">
        <div className="flex flex-wrap gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === section.id
                  ? 'bg-yellow-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {section.icon} {section.name}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pb-24">
        {renderSection()}
      </div>

      {/* Bottom Navigation */}
    </div>
  );
};

export default ManifestPage;

